"use client";

import { useIsMobile } from "@/hooks/useIsMobile";
import { useKeyPressed } from "@/hooks/useKeyPressed";
import { useLockBodyScroll } from "@/hooks/useLockBodyScroll";
import { useOnClickOutside } from "@/hooks/useOnClickOutside";
import { byPrefixAndName } from "@awesome.me/kit-12cf79935c/icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import clsx from "clsx";
import { useTranslationDetector } from "@/hooks/useTranslationDetector";
import { createPortal } from "react-dom";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

/**
 * GenericDropdown
 * - Modeled after GenericAutoComplete UX
 * - No typing; shows a list of options to pick
 * - Works both on web (popover) and mobile (full-screen portal)
 */
const GenericDropdown = ({
  id,
  label,
  value,
  onSelect,
  options = [], // [{ value, label }] or string[]
  placeholder, // ignored to allow floating label to work as intended
  error,
  translationNamespace = "common",
  onOpen,
  className,
  // Optional mapping to control display text
  getOptionLabel,
  getOptionValue,
  // Sizing (accepts number => px or any CSS size string)
  // Class overrides
  triggerClassName,
  dropdownClassName,
  buttonClassName,
  scrollToForm,
}) => {
  const isMobile = useIsMobile();
  const t = useTranslationDetector(translationNamespace);

  // Normalize options to { value, label }
  const normalizedOptions = useMemo(() => {
    return options.map((opt) => {
      if (typeof opt === "object") return { value: opt.Id, label: opt.Name };
      return { value: opt, label: String(opt) };
    });
  }, [options]);

  const getLabel = useCallback(
    (opt) => {
      if (getOptionLabel) return getOptionLabel(opt);
      if (!opt) return "";
      if (typeof opt === "object") return opt.label ?? String(opt.value ?? "");
      return String(opt);
    },
    [getOptionLabel],
  );

  const getValue = useCallback(
    (opt) => {
      if (getOptionValue) return getOptionValue(opt);
      if (!opt) return "";
      if (typeof opt === "object") return opt.value ?? "";
      return String(opt);
    },
    [getOptionValue],
  );

  const selectedValue = useMemo(() => getValue(value), [value, getValue]);
  const selectedLabel = useMemo(() => getLabel(value), [value, getLabel]);

  // Open/close + portal states (same pattern as GenericAutoComplete)
  const [isOpen, setIsOpen] = useState(false);
  const [shouldRenderPortal, setShouldRenderPortal] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const [shouldRenderWebDropdown, setShouldRenderWebDropdown] = useState(false);
  const [isWebDropdownAnimating, setIsWebDropdownAnimating] = useState(false);
  const escapePressed = useKeyPressed("Escape");
  const containerRef = useRef(null);

  // Derived flags (must come after isOpen is declared)
  const hasValue = !!selectedLabel && selectedLabel !== "";
  const shouldFloat = isOpen || hasValue || (error && isMobile);

  const handleClickOutside = useCallback(() => {
    if (!isMobile && isOpen) {
      setIsOpen(false);
    }
  }, [isMobile, isOpen]);

  useOnClickOutside(containerRef, handleClickOutside);
  useLockBodyScroll(shouldRenderPortal && isMobile);

  useEffect(() => {
    if (escapePressed && isOpen) {
      setIsOpen(false);
    }
  }, [escapePressed, isOpen]);

  // Portal animations
  useEffect(() => {
    if (isOpen && isMobile) {
      setShouldRenderPortal(true);
      const timer = setTimeout(() => setIsAnimating(true), 10);
      return () => clearTimeout(timer);
    } else if (!isOpen && isMobile) {
      setIsAnimating(false);
      const timer = setTimeout(() => setShouldRenderPortal(false), 300);
      return () => clearTimeout(timer);
    }
  }, [isOpen, isMobile]);

  // Web dropdown animations
  useEffect(() => {
    if (isOpen && !isMobile) {
      setShouldRenderWebDropdown(true);
      const timer = requestAnimationFrame(() => {
        requestAnimationFrame(() => setIsWebDropdownAnimating(true));
      });
      return () => cancelAnimationFrame(timer);
    } else if (!isOpen && !isMobile) {
      setIsWebDropdownAnimating(false);
      const timer = setTimeout(() => setShouldRenderWebDropdown(false), 200);
      return () => clearTimeout(timer);
    }
  }, [isOpen, isMobile]);

  const containerClasses = useMemo(
    () =>
      clsx(
        "border-primary-weak h-[68px] duration-300 ease-in-out md:h-[66px] rounded-none md:rounded-lg md:border-0",
        isOpen && !error && "md:ring-primary md:ring-2",
        error && "!rounded-lg ring-2 ring-red-500",
        triggerClassName,
        className,
      ),
    [isOpen, error, className, triggerClassName],
  );

  const labelClasses = useMemo(
    () =>
      clsx(
        "absolute left-[18px] font-medium transition-all duration-300 ease-in-out md:left-5",
        error ? "text-red-500" : "text-foreground-muted",
        // Float when open or a value is selected
        shouldFloat
          ? "top-[11.5px] text-xs"
          : "top-1/2 -translate-y-1/2 text-base",
      ),
    [shouldFloat, error, isMobile],
  );

  const buttonClasses = useMemo(
    () =>
      clsx(
        "h-full w-full truncate rounded-lg border-0 bg-transparent px-[18px] text-left font-semibold transition-all duration-300 ease-in-out focus:ring-0 focus:outline-none md:px-5",
        hasValue
          ? "pt-[26px] pb-[12px] md:pt-[31.5px] md:pb-[13.5px]"
          : "py-[12px] md:py-[22.5px]",
        buttonClassName,
      ),
    [hasValue, buttonClassName],
  );

  const overlayClasses = useMemo(() => {
    return clsx(
      "fixed inset-0 z-50 bg-black/50",
      "transition-opacity duration-300",
      isAnimating ? "opacity-100" : "opacity-0",
    );
  }, [isAnimating]);

  const portalContainerClasses = useMemo(() => {
    return clsx(
      "fixed right-0 bottom-0 left-0 z-50 flex flex-col rounded-t-2xl bg-background-global",
      "transform transition-all duration-300",
      isAnimating ? "translate-y-0 ease-out" : "translate-y-full ease-in",
    );
  }, [isAnimating]);

  const webDropdownClasses = useMemo(() => {
    return clsx(
      "shadow-xxl absolute top-full left-0 z-50 mt-2 w-[475px] rounded-lg bg-white",
      "origin-top transform transition-all duration-200 ease-out",
      "scrollbar-hide max-h-[425px] overflow-y-auto",
      isWebDropdownAnimating
        ? "translate-y-0 scale-100 opacity-100"
        : "-translate-y-2 scale-95 opacity-0",
      dropdownClassName,
    );
  }, [isWebDropdownAnimating, dropdownClassName]);

  const openDropdown = useCallback(() => {
    // İlk açılışta (desktop) formu yukarı kaydır
    if (!isOpen) {
      if (!isMobile && scrollToForm && containerRef.current) {
        const inputRect = containerRef.current.getBoundingClientRect();
        const scrollTop =
          window.pageYOffset || document.documentElement.scrollTop;
        const targetPosition = inputRect.top + scrollTop - 200;

        window.scrollTo({
          top: targetPosition,
          behavior: "smooth",
        });
      }

      if (onOpen) onOpen();
    }

    setIsOpen((prev) => !prev);
  }, [isOpen, isMobile, scrollToForm, onOpen]);

  const handleSelectOption = useCallback(
    (opt) => {
      onSelect?.(opt);
      setIsOpen(false);
    },
    [onSelect],
  );

  const renderMobileOptions = useCallback(
    () => (
      <div className="flex flex-col gap-3">
        {normalizedOptions.map((opt) => (
          <button
            key={opt.value}
            className={clsx(
              "flex cursor-pointer items-center gap-3 py-3 px-4 text-left transition-colors bg-white rounded-lg",
            )}
            onClick={() => handleSelectOption(opt)}
          >
            {/* Radio button circle - FerryVehicleSelect design */}
            <span
              className={clsx(
                "relative h-6 w-6 rounded-full border flex-shrink-0 transition-all duration-200 ease-in-out",
                selectedValue === opt.value
                  ? "bg-primary border-primary"
                  : "border-primary-weak bg-white",
              )}
            >
              <span
                className={clsx(
                  "absolute top-1/2 left-1/2 h-0 w-0 origin-center -translate-x-1/2 -translate-y-1/2 rounded-full bg-white transition-all duration-200 ease-in-out",
                  selectedValue === opt.value && "h-2 w-2",
                )}
              />
            </span>
            {/* Label */}
            <span className="text-base">{opt.label}</span>
          </button>
        ))}
      </div>
    ),
    [normalizedOptions, selectedValue, handleSelectOption],
  );

  const renderDesktopOptions = useCallback(
    () => (
      <div className="flex flex-col">
        {normalizedOptions.map((opt) => (
          <button
            key={opt.value}
            className={clsx(
              "flex cursor-pointer items-start justify-between gap-6 text-left transition-colors bg-white px-5 py-2",
              selectedValue === opt.value && "font-semibold",
              "hover:bg-primary-weak/50",
            )}
            onClick={() => handleSelectOption(opt)}
          >
            <div className="flex items-center gap-4">
              <div className="flex flex-col items-start gap-1">
                <div className="flex items-center justify-start gap-[6px]">
                  {opt.label}
                </div>
              </div>
            </div>
          </button>
        ))}
      </div>
    ),
    [normalizedOptions, selectedValue, handleSelectOption],
  );

  return (
    <div className="relative" ref={containerRef}>
      <div className={containerClasses} onClick={openDropdown}>
        {label && <label className={labelClasses}>{label}</label>}
        <button
          type="button"
          id={id}
          className={buttonClasses}
          style={{ paddingRight: 35 }}
        >
          {selectedLabel}
        </button>
        <span className="pointer-events-none absolute top-1/2 right-4 -translate-y-1/2">
          <FontAwesomeIcon className="text-foreground-subtle" icon={byPrefixAndName.fas["chevron-down"]} />
        </span>
      </div>

      {/* Web dropdown */}
      {!isMobile && shouldRenderWebDropdown && (
        <div className={webDropdownClasses}>{renderDesktopOptions()}</div>
      )}

      {/* Mobile portal */}
      {shouldRenderPortal &&
        isMobile &&
        createPortal(
          <>
            <div className={overlayClasses} onClick={() => setIsOpen(false)} />
            <div className={portalContainerClasses}>
              <div className="flex shrink-0 items-center justify-between px-4 py-5">
                <span className="w-full text-center text-lg font-semibold">
                  {label}
                </span>
                <button onClick={() => setIsOpen(false)} className="self-end">
                  <FontAwesomeIcon
                    icon={byPrefixAndName.far["xmark"]}
                    className="text-2xl"
                  />
                </button>
              </div>
              <div className="scrollbar-hide h-[calc(100%-60px)] flex-1 touch-pan-y overflow-y-auto overscroll-contain px-4 pb-4">
                {renderMobileOptions()}
              </div>
            </div>
          </>,
          document.body,
        )}

      {error && (
        <p className="text-danger absolute -mt-8 flex items-center gap-1 pl-4 md:mt-1 md:text-xs">
          <FontAwesomeIcon icon={byPrefixAndName.far["circle-exclamation"]} />
          <span>{error}</span>
        </p>
      )}
    </div>
  );
};

export default GenericDropdown;
